import { useEffect, useState } from 'react'
import { api } from '../api/client'

/*
  Forwarding mail to the board.

  The screen has one job before any of the others: get the address into the owner's phone.
  Nobody types an address into a forward field from memory, so an address they cannot copy in
  one tap is an address they will use once. Hence the copy buttons and the contact card, which
  are the whole feature as far as adoption is concerned.

  The permalink is claimed once and never changes. That is stated on the screen before it is
  claimed rather than discovered afterwards, because by the time it matters the address is in
  somebody's accountant's address book.
*/

interface Sender {
  id: string
  email: string
  verifiedAt: string | null
  createdAt: string
  isVerified: boolean
}

interface Inbound {
  id: string
  fromAddress: string | null
  subject: string | null
  status: string
  rejectReason: string | null
  agendaItemId: string | null
  receivedAt: string
}

interface Settings {
  configured: boolean
  permalink: string | null
  domain: string
  address: string | null
  askAddress: string | null
  senders: Sender[]
  recent: Inbound[]
}

export function EmailCapture() {
  const [state, setState] = useState<Settings | null>(null)
  const [error, setError] = useState('')

  const load = async () => {
    try {
      setState(await api.get<Settings>('/api/inbound/settings'))
    } catch {
      setError('Could not load your email settings.')
    }
  }

  useEffect(() => { void load() }, [])

  if (!state) return null

  return (
    <div className="card card--rows">
      <div className="card__title">Email capture</div>

      {error && <div className="signin__error">{error}</div>}

      {state.address
        ? <Addresses address={state.address} askAddress={state.askAddress!} />
        : state.configured
          ? <Claim domain={state.domain} onClaimed={load} />
          : <NotYet />}

      {state.address && (
        <>
          <Senders senders={state.senders} onChanged={load} />
          <Recent recent={state.recent} />
        </>
      )}
    </div>
  )
}

/*
  Before the mail route exists.

  The name is claimed once and never changes, so showing the form on a deployment that
  cannot receive anything would let somebody spend the one name they get on an address that
  bounces. Better to say plainly that it is not on yet.
*/
function NotYet() {
  return (
    <p className="muted">
      Email capture is not switched on yet. When it is, your board gets its own address and
      anything you forward to it lands on The Table with the thread and its attachments
      filed.
    </p>
  )
}

// ─── Claiming the address ───────────────────────────────────────────────────

function Claim({ domain, onClaimed }: { domain: string; onClaimed: () => Promise<void> }) {
  const [name, setName] = useState('')
  const [check, setCheck] = useState<{ available: boolean; message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  /*
    Checked while it is being typed, 400ms after the typing stops.

    Not on every keystroke: the answer for "h", "ha" and "hal" is noise, and a field that
    flashes red at somebody three characters into their own company name reads as a
    complaint rather than help.
  */
  useEffect(() => {
    const value = name.trim()
    if (value.length < 3) { setCheck(null); return }

    const timer = setTimeout(async () => {
      try {
        setCheck(await api.get(`/api/inbound/permalink/available?name=${encodeURIComponent(value)}`))
      } catch {
        setCheck(null)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [name])

  const claim = async () => {
    setSaving(true)
    setError('')
    try {
      await api.post('/api/inbound/permalink', { permalink: name.trim() })
      await onClaimed()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="capture__claim">
      <p className="muted">
        Pick a name and your board gets its own address. Forward anything to it and it lands
        on The Table with the thread and its attachments filed.
      </p>

      <div className="capture__field">
        <input
          className="input capture__input"
          value={name}
          onChange={e => setName(e.target.value.toLowerCase())}
          placeholder="halvorsen"
          maxLength={32}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Your address"
        />
        <span className="capture__domain">@{domain}</span>
      </div>

      {check && (
        <div className={check.available ? 'capture__ok' : 'capture__no'}>
          {check.available ? 'That one is free.' : check.message}
        </div>
      )}

      {error && <div className="capture__no">{error}</div>}

      <p className="dim">
        This is permanent. Once it is in an accountant's address book it cannot be changed,
        so it is worth a moment: your company name is usually the right answer.
      </p>

      {/*
        Live as soon as there is something to claim, rather than waiting on the check.

        The check is a courtesy that answers most of the time; ClaimAsync is the guard and
        re-runs both the shape and the uniqueness test. Gating the button on a request in
        flight means the resting state of this screen is a dead button, and somebody who
        typed their company name and got no response reads that as broken rather than as
        pending.
      */}
      <button
        className="btn btn--primary"
        disabled={saving || name.trim().length < 3 || check?.available === false}
        onClick={claim}
      >
        {saving ? 'Claiming…' : 'Claim this address'}
      </button>
    </div>
  )
}

// ─── The two addresses ──────────────────────────────────────────────────────

function Addresses({ address, askAddress }: { address: string; askAddress: string }) {
  return (
    <div className="capture__addresses">
      <Address
        value={address}
        label="Forward anything here"
        note="It becomes an agenda item for the next meeting."
      />
      <Address
        value={askAddress}
        label="Need an answer today"
        note="The chair replies within the hour instead of waiting for the meeting."
      />
      <VCard address={address} />
    </div>
  )
}

function Address({ value, label, note }: { value: string; label: string; note: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard access is refused in enough contexts that failing loudly would be worse
      // than the address simply staying on screen where it can be selected by hand.
    }
  }

  return (
    <div className="capture__address">
      <div className="capture__label">{label}</div>
      <div className="capture__row">
        <code className="capture__value">{value}</code>
        <button className="btn btn--small" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <div className="dim">{note}</div>
    </div>
  )
}

/*
  A contact card, because the address has to end up in the phone.

  Forwarding on iOS means typing into a To field that autocompletes against contacts and
  nothing else. An address that is not a contact has to be typed correctly, on a phone, from
  memory, which is the difference between a feature people use and one they tried once.
*/
function VCard({ address }: { address: string }) {
  const download = () => {
    const card = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      'N:Board;My;;;',
      'FN:My Board',
      'ORG:Boardium',
      `EMAIL;TYPE=INTERNET;TYPE=PREF:${address}`,
      'END:VCARD',
      '',
    ].join('\r\n')

    const url = URL.createObjectURL(new Blob([card], { type: 'text/vcard' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'my-board.vcf'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <button className="btn btn--quiet btn--small" onClick={download}>
      Save as a contact
    </button>
  )
}

// ─── Who may forward ────────────────────────────────────────────────────────

function Senders({ senders, onChanged }: { senders: Sender[]; onChanged: () => Promise<void> }) {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState('')

  const add = async () => {
    const value = email.trim()
    if (!value) return

    setBusy(true)
    setSaid('')
    try {
      const result = await api.post<{ alreadyVerified?: boolean; emailed?: boolean }>(
        '/api/inbound/senders', { email: value })

      setSaid(result.alreadyVerified
        ? 'That address can already forward.'
        : result.emailed === false
          ? 'Added, but we could not send the confirmation. Try again in a minute.'
          : 'Sent. They have to click the link before their forwards arrive.')

      setEmail('')
      await onChanged()
    } catch (e) {
      setSaid(e instanceof Error ? e.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const remove = async (sender: Sender) => {
    if (!confirm(`Stop accepting forwards from ${sender.email}?`)) return
    await api.del(`/api/inbound/senders/${sender.id}`)
    await onChanged()
  }

  return (
    <div className="capture__senders">
      <div className="capture__label">Who may forward</div>
      <p className="dim">
        Only these addresses. Anything else is refused, which is what stops a guessed address
        putting a stranger's mail in front of your board.
      </p>

      <ul className="capture__list">
        {senders.map(s => (
          <li key={s.id} className="capture__sender">
            <span className="capture__sender-email">{s.email}</span>
            <span className="capture__side">
              {s.isVerified
                ? <span className="chip chip--support">Verified</span>
                : <span className="chip chip--conditional">Waiting</span>}
              <button className="btn btn--small btn--quiet" onClick={() => remove(s)}>Remove</button>
            </span>
          </li>
        ))}
      </ul>

      <div className="capture__row">
        <input
          className="input capture__input"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="dana@yourcompany.com"
          aria-label="Address to allow"
        />
        <button className="btn btn--small" disabled={busy || !email.trim()} onClick={add}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>

      {said && <div className="dim">{said}</div>}
    </div>
  )
}

// ─── What arrived ───────────────────────────────────────────────────────────

/*
  The last ten, and what became of each.

  This exists for one moment: somebody forwards a thread, nothing appears on The Table, and
  they have no way to tell whether the product lost it or never got it. Showing the refusal
  and its reason answers that in one look, which is the difference between a bug report and
  an address they add to the allowlist.
*/
function Recent({ recent }: { recent: Inbound[] }) {
  if (recent.length === 0) return null

  return (
    <div className="capture__recent">
      <div className="capture__label">Recently forwarded</div>
      <ul className="capture__list">
        {recent.map(r => (
          <li key={r.id} className="capture__inbound">
            <div className="capture__inbound-main">
              <span className="capture__inbound-subject">{r.subject || '(no subject)'}</span>
              <span className="dim">{r.fromAddress || 'unknown sender'}</span>
            </div>
            <span className="capture__side">
              <Outcome row={r} />
              <span className="dim">{when(r.receivedAt)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Outcome({ row }: { row: Inbound }) {
  if (row.status === 'processed') return <span className="chip chip--support">On The Table</span>
  if (row.status === 'rejected') return <span className="chip chip--oppose">{refusal(row.rejectReason)}</span>
  if (row.status === 'failed') return <span className="chip chip--oppose">Could not read it</span>
  return <span className="chip chip--conditional">Working on it</span>
}

/** Plain English, never the stored reason code. */
function refusal(reason: string | null): string {
  switch (reason) {
    case 'unverified_sender': return 'Sender not allowed'
    case 'spf_failed':
    case 'dkim_failed':       return 'Could not verify sender'
    case 'too_large':         return 'Too large'
    case 'rate_limited':      return 'Too many today'
    case 'inactive_business': return 'Account inactive'
    default:                  return 'Not accepted'
  }
}

/*
  receivedAt is an instant, not a calendar day, so it is parsed as one.

  src/dates.ts is for the other kind and would be wrong here: it reads a value as a local
  calendar day deliberately, which is right for a due date and wrong for the moment a
  message landed.
*/
function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
